/**
 * User Repository
 * Handles all user-related API calls
 */

import { apiClient } from '../client'
import type { PaginatedResponse, Role } from './role.repository'
import { Tenant } from './tenant.repository'
import type { Row } from '@tanstack/react-table'

export interface User {
    id: number
    name?: string
    en_name?: string
    ar_name?: string
    username?: string
    email: string
    phone?: string
    user_type?: string
    password?: string
    password_hash?: string
    nationality?: string
    national_id?: string
    dob?: Date | null
    gender?: string
    marital_status?: string
    employee_code?: string
    joining_date?: Date | null
    contract_type?: number
    access_to?: number
    position?: number
    department?: string
    job_grade?: number
    role_id?: number
    report_to?: number
    last_login?: Date | null
    login_attempts?: number
    status?: number
    geo_fence?: number
    vpn_check?: number
    auto_visit_close?: number
    fake_gps_check?: number
    is_compress?: number
    image_quality?: number
    geo_fence_range?: string
    created_by?: string
    updated_by?: string
    region?: string
    city?: string
    user_region: string;
    user_city: string;
    resident_region?: string;
    resident_city?: string
    /** Relative path (e.g. profile_pics/…) or full URL; combined with static_bucket_url when not absolute. */
    profile_pic?: string | null
    /** Tenant static bucket base URL (from user detail query). */
    static_bucket_url?: string | null
    role?: Role
    // Comma-separated ID strings (from database)
    country_ids?: string | null
    region_ids?: string | null
    city_ids?: string | null
    area_ids?: string | null
    chain_ids?: string | null
    channel_ids?: string | null
    subchannel_ids?: string | null
    store_format_ids?: string | null
    store_class_ids?: string | null
    trade_type_ids?: string | null
    store_ids?: string | null
    client_ids?: string | null
    department_ids?: string | null
    principal_ids?: string | null
    segment_ids?: string | null
    sector_ids?: string | null
    category_ids?: string | null
    subcategory_ids?: string | null
    brand_ids?: string | null
    sku_group_ids?: string | null
    sku_ids?: string | null
    // Entity arrays (from backend service)
    countries?: any[] | null
    regions?: any[] | null
    cities?: any[] | null
    areas?: any[] | null
    chains?: any[] | null
    channels?: any[] | null
    subchannels?: any[] | null
    storeFormats?: any[] | null
    storeClasses?: any[] | null
    tradeTypes?: any[] | null
    clients?: any[] | null
    departments?: any[] | null
    principals?: any[] | null
    segments?: any[] | null
    sectors?: any[] | null
    categories?: any[] | null
    subcategories?: any[] | null
    brands?: any[] | null
    skuGroups?: any[] | null
    created_at?: Date
    updated_at?: Date
    tenants?: Tenant[]
    bu_ids?: string
    row: {}
}

export type BulkChangeColumn = {
    key: string
    label: string
    type: 'string' | 'int' | 'date'
}

export interface CreateUserRequest {
    name?: string
    en_name?: string
    ar_name?: string
    username: string
    email: string
    phone?: string
    user_type?: string
    password?: string
    password_hash?: string
    nationality_id?: string
    national?: number
    user_region?: number;
    user_city?: number;
    dob?: Date | null
    gender?: string
    marital_status?: string
    employee_code?: string
    joining_date?: Date | null
    contract_type?: number
    position?: number
    department?: string
    job_grade?: number
    role_id?: number
    report_to?: number
    access_to?: number
    // Comma-separated ID strings for backend
    country_ids?: string
    region_ids?: string
    city_ids?: string
    area_ids?: string
    chain_ids?: string
    channel_ids?: string
    subchannel_ids?: string
    store_format_ids?: string
    store_class_ids?: string
    trade_type_ids?: string
    store_ids?: string
    client_ids?: string
    department_ids?: string
    principal_ids?: string
    segment_ids?: string
    sector_ids?: string
    category_ids?: string
    subcategory_ids?: string
    brand_ids?: string
    sku_group_ids?: string
    sku_ids?: string
    bu_ids?: string
    geo_fence?: number
    vpn_check?: number
    auto_visit_close?: number
    fake_gps_check?: number
    geo_fence_range?: string

}

export interface UpdateUserRequest {
    name?: string
    en_name?: string
    ar_name?: string
    username?: string
    email?: string
    phone?: string
    user_type?: string
    password?: string
    password_hash?: string
    nationality_id?: string
    national?: number
    user_region?: number;
    user_city?: number;
    dob?: Date | null
    gender?: string
    marital_status?: string
    employee_code?: string
    joining_date?: Date | null
    contract_type?: number
    position?: number
    department?: string
    job_grade?: number
    role_id?: number
    report_to?: number
    access_to?: number
    // Comma-separated ID strings for backend
    country_ids?: string
    region_ids?: string
    city_ids?: string
    area_ids?: string
    chain_ids?: string
    channel_ids?: string
    subchannel_ids?: string
    store_format_ids?: string
    store_class_ids?: string
    trade_type_ids?: string
    store_ids?: string
    client_ids?: string
    department_ids?: string
    principal_ids?: string
    segment_ids?: string
    sector_ids?: string
    category_ids?: string
    subcategory_ids?: string
    brand_ids?: string
    sku_group_ids?: string
    sku_ids?: string
    bu_ids?: string
    status?: number
    geo_fence?: number
    vpn_check?: number
    auto_visit_close?: number
    fake_gps_check?: number

}

class UserRepository {
    /**
     * Get all users with pagination
     */
    async getAll(page: number = 1, limit: number = 25, filters?: any): Promise<PaginatedResponse<User>> {
        const params = new URLSearchParams({
            page: page.toString(),
            limit: limit.toString(),
        })

        // Add filters if provided
        if (filters) {
            if (filters.search) {
                params.append('search', filters.search)
            }
            if (filters.status) {
                params.append('status', filters.status)
            }
            if (filters.user_type) {
                params.append('user_type', filters.user_type)
            }
            if (filters.roleId) {
                params.append('role_id', filters.roleId)
            }
            if (filters.regionId) {
                params.append('region_id', filters.regionId)
            }
            if (filters.cityId) {
                params.append('city_id', filters.cityId)
            }
        }

        return apiClient.get<PaginatedResponse<User>>(`/users?${params.toString()}`)
    }


    async getDasboardData() {
        return apiClient.get('/users/dashboard-data');
    }

    /**
     * Get user by ID
     */
    async getById(id: number): Promise<User> {
        return apiClient.get<User>(`/users/${id}`)
    }
    async getByUsername(username: string): Promise<User> {
        return apiClient.get<User>(`/users/by-username/${username}`)
    }


    /**
     * Create a new user
     */
    async create(data: CreateUserRequest): Promise<User> {
        return apiClient.post<User>('/users', data)
    }

    /**
     * Update an existing user
     */
    async update(id: number, data: UpdateUserRequest): Promise<User> {
        return apiClient.put<User>(`/users/${id}`, data)
    }

    async updateProfilePic(id: number, data: {
        profile_pic: string
    }): Promise<User> {
        return apiClient.put<User>(`/users/${id}/profile-pic`, data)
    }

    /**
     * Delete a user
     */
    async delete(id: number): Promise<{ message: string }> {
        return apiClient.delete<{ message: string }>(`/users/${id}`)
    }

    // RLS Methods
    async getProductRLS(userId: number) {
        return apiClient.get(`/users/${userId}/product-rls`)
    }

    async updateProductRLS(userId: number, data: {
        client_ids?: number[]
        department_ids?: number[]
        principal_ids?: number[]
        segment_ids?: number[]
        sector_ids?: number[]
        category_ids?: number[]
        subcategory_ids?: number[]
        brand_ids?: number[]
        sku_group_ids?: number[]
        bu_ids?: number[]
    }) {
        return apiClient.post(`/users/${userId}/product-rls`, data)
    }

    async getUniverseRLS(userId: number) {
        return apiClient.get(`/users/${userId}/universe-rls`)
    }

    async updateUniverseRLS(userId: number, data: {
        country_ids?: number[]
        region_ids?: number[]
        city_ids?: number[]
        area_ids?: number[]
    }) {
        return apiClient.post(`/users/${userId}/universe-rls`, data)
    }

    async getStoreRLS(userId: number) {
        return apiClient.get(`/users/${userId}/store-rls`)
    }

    async updateStoreRLS(userId: number, data: {
        store_ids?: number[]
        chain_ids?: number[]
        channel_ids?: number[]
        subchannel_ids?: number[]
        store_format_ids?: number[]
        store_class_ids?: number[]
        trade_type_ids?: number[]
    }) {
        return apiClient.post(`/users/${userId}/store-rls`, data)
    }

    // Employment Information Methods
    async getEmploymentInformation(userId: number) {
        return apiClient.get(`/users/${userId}/employment-information`)
    }

    async updateEmploymentInformation(userId: number, data: {
        employee_code?: string
        joining_date?: string | Date | null
        contract_type?: number
        position?: string
        department?: string
    }) {
        return apiClient.post(`/users/${userId}/employment-information`, data)
    }

    /**
     * Export users to Excel
     */
    async exportToExcel(filters?: any): Promise<Blob> {
        const params = new URLSearchParams()
        if (filters) {
            if (filters.search) params.append('search', filters.search)
            if (filters.status) params.append('status', filters.status)
            if (filters.roleId) {
                params.append('role_id', filters.roleId)
            }
            if (filters.regionId) {
                params.append('region_id', filters.regionId)
            }
            if (filters.cityId) {
                params.append('city_id', filters.cityId)
            }
        }

        const baseUrl = `/users/export/excel`;
        const queryString = params.toString();
        const url = queryString ? `${baseUrl}?${queryString}` : baseUrl;

        return apiClient.getBlob(url, true, { showSuccessToast: true, successMessage: 'Exported Successfully' })
    }

    async exportTemplateToExcel(): Promise<Blob> {
        return apiClient.getBlob('/users/export/template', true, { showSuccessToast: true, successMessage: 'Exported Successfully' })
    }

    /**
     * Import users from Excel file
     */
    async importFromExcel(file: File): Promise<{
        success: boolean;
        message: string;
        data: {
            success: boolean;
            total: number;
            created: number;
            updated: number;
            errors: Array<{ row: number; message: string }>;
        };
    }> {
        const formData = new FormData()
        formData.append('file', file)
        return apiClient.post('/users/import/excel', formData)
    }

    async getBulkChangeColumns(): Promise<BulkChangeColumn[]> {
        return apiClient.get('/users/bulk-change-by-column/columns')
    }

    async exportBulkChangeTemplate(): Promise<Blob> {
        return apiClient.getBlob('/users/bulk-change-by-column/template')
    }

    async bulkChangeByColumn(file: File, column: string): Promise<{
        success: boolean
        message: string
        data: {
            success: boolean
            total: number
            updated: number
            errors: Array<{ row: number; message: string }>
        }
    }> {
        const formData = new FormData()
        formData.append('file', file)
        formData.append('column', column)
        return apiClient.post(`/users/bulk-change-by-column?column=${encodeURIComponent(column)}`, formData)
    }
}

// Export singleton instance

export const userRepository = new UserRepository()

